import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { environment } from '@utility/app.util';

import Echo from 'laravel-echo';
import Pusher from 'pusher-js';

@Injectable({ providedIn: 'root' })
export class EchoService implements OnDestroy {
  private echo: Echo<'pusher'> | null = null;
  private helper = new JwtHelperService();

  constructor(private readonly zone: NgZone) {}

  public getEcho(): Echo<'pusher'> | null {
    if (!this.echo) {
      this.initEcho();
    }
    return this.echo;
  }

  public listenOnPrivateChannel(
    channelName: string,
    eventName: string,
    callback: (data: any) => void,
    lifecycle?: { subscribed: () => void; error: () => void },
  ): void {
    const zoneCallback = (data: any): void => {
      this.zone.run(() => callback(data));
    };

    const channel = this.getEcho()?.private(channelName);
    channel?.listen(eventName, zoneCallback);
    if (lifecycle) {
      channel?.subscribed(() => this.zone.run(lifecycle.subscribed));
      channel?.error(() => this.zone.run(lifecycle.error));
    }

    if ((window as any).Cypress) {
      const listeners = (window as any).__sociuuEchoTestListeners ?? {};
      listeners[channelName] = listeners[channelName] ?? {};
      listeners[channelName][eventName] = zoneCallback;
      (window as any).__sociuuEchoTestListeners = listeners;
    }
  }

  public leaveChannel(channelName: string): void {
    this.echo?.leave(channelName);
    if ((window as any).Cypress) {
      delete (window as any).__sociuuEchoTestListeners?.[channelName];
    }
  }

  public resetEchoAuth(newToken?: string): void {
    const authorization = newToken ? `Bearer ${newToken}` : null;
    const authHeaders = (this.echo as any)?.options?.auth?.headers;
    const connectorAuthHeaders = (this.echo as any)?.connector?.pusher?.config?.auth?.headers;

    if (authHeaders) {
      if (authorization) {
        authHeaders.Authorization = authorization;
      } else {
        delete authHeaders.Authorization;
      }
    }

    if (connectorAuthHeaders) {
      if (authorization) {
        connectorAuthHeaders.Authorization = authorization;
      } else {
        delete connectorAuthHeaders.Authorization;
      }
    }

    if (!newToken && this.echo) {
      this.echo.disconnect();
      this.echo = null;
    }
  }

  public getClientIdFromToken(): number | null {
    const token = localStorage.getItem('token');
    if (!token) {
      return null;
    }

    try {
      const decoded = this.helper.decodeToken(token);
      return decoded?.client_id ?? null;
    } catch {
      return null;
    }
  }

  public isWebSocketConnected(): boolean {
    try {
      return (this.echo?.connector as any)?.pusher?.connection?.state === 'connected';
    } catch {
      return false;
    }
  }

  public ngOnDestroy(): void {
    if (this.echo) {
      this.echo.disconnect();
      this.echo = null;
    }
  }

  private initEcho(): void {
    const token = localStorage.getItem('token');
    if (!token || !environment.reverbKey) {
      return;
    }

    // Make Pusher available globally (required by laravel-echo)
    (window as any).Pusher = Pusher;

    this.echo = new Echo({
      broadcaster: 'pusher',
      key: environment.reverbKey,
      wsHost: environment.reverbHost,
      wsPort: environment.reverbPort,
      wssPort: environment.reverbPort,
      forceTLS: environment.reverbScheme === 'https',
      disableStats: true,
      enabledTransports: ['ws', 'wss'],
      cluster: 'mt1',
      authEndpoint: `${environment.baseUrl}/broadcasting/auth`,
      auth: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    });
  }
}
